export type DataErrorCode =
  | 'no_bucket'
  | 'unauthenticated'
  | 'forbidden'
  | 'conflict'
  | 'not_found'
  | 'invalid'
  | 'network'
  | 'unknown';

export class DataError extends Error {
  readonly code: DataErrorCode;
  override readonly cause: unknown;

  constructor(code: DataErrorCode, message: string, cause?: unknown) {
    super(message);
    this.name = 'DataError';
    this.code = code;
    this.cause = cause;
  }
}

interface ErrorLike {
  message: string;
  code?: string;
  status?: number;
}

function isErrorLike(value: unknown): value is ErrorLike {
  return typeof value === 'object' && value !== null && typeof (value as { message?: unknown }).message === 'string';
}

/** Maps PostgREST / Auth / fetch errors onto a small, UI-friendly set of codes. */
export function toDataError(error: unknown): DataError {
  if (error instanceof DataError) return error;
  if (!isErrorLike(error)) return new DataError('unknown', String(error), error);

  const code = typeof error.code === 'string' ? error.code : '';
  const message = error.message;

  // Storage returns a plain 400/404 "Bucket not found" when the work-files bucket has not been
  // created on this project yet (supabase/migrations/20261001000000_task_attachments_storage.sql
  // not pushed). That is a deployment step, not a user error, so it gets its own code and the UI
  // can say what to run instead of blaming the file.
  if (/bucket not found/i.test(message)) {
    return new DataError('no_bucket', message, error);
  }

  if (code === '42501' || code === 'PGRST301' || /permission denied|row-level security/i.test(message)) {
    return new DataError('forbidden', message, error);
  }
  if (code === '28000' || code === 'PGRST303' || error.status === 401 || /jwt|not authenticated/i.test(message)) {
    return new DataError('unauthenticated', message, error);
  }
  if (code === '22023' || code === '23502' || code === '23503' || code === '23514' || code === 'P0001') {
    return new DataError('invalid', message, error);
  }
  if (code === '23505') return new DataError('conflict', message, error);
  if (code === 'PGRST116') return new DataError('not_found', message, error);
  if (error instanceof TypeError || /failed to fetch|network|load failed|timeout/i.test(message)) {
    return new DataError('network', message, error);
  }
  return new DataError('unknown', message, error);
}
